export const portfolioData = {
  name: "Natnael Desalegn",
  title: "AI & ML Engineer",
  tagline:
    "I build production-ready GenAI + ML systems: RAG chatbots, data pipelines, model deployment, and cloud automation on AWS.",
  location: "Krakow, Poland (Remote)",
  email: "natnaela@gmail.com",
  highlights: [
    { k: "3+ years", v: "ML + Data Engineering" },
    { k: "Production GenAI", v: "RAG, tuning, prompt optimization" },
    { k: "AWS Certified", v: "Cloud-native AI deployments" },
    { k: "MLOps", v: "CI/CD, Docker, FastAPI" },
  ],
  skills: [
    {
      group: "GenAI & LLM",
      items: [
        "RAG Architecture",
        "LangChain",
        "Prompt Engineering",
        "LLM Fine-tuning",
        "Vector Search",
      ],
    },
    {
      group: "ML / CV / NLP",
      items: [
        "PyTorch",
        "TensorFlow",
        "Scikit-learn",
        "OpenCV",
        "XgBoost",
        "Feature Engineering",
        "Evaluation & Tuning",
      ],
    },
    {
      group: "Data Engineering",
      items: [
        "PySpark",
        "Pandas",
        "ETL/ELT",
        "Data Validation",
        "SQL",
        "Databricks (familiar)",
      ],
    },
    {
      group: "Cloud & MLOps",
      items: [
        "AWS (S3/EC2/IAM/Lambda)",
        "Terraform",
        "Docker",
        "Jenkins",
        "CI/CD",
        "FastAPI Deployment",
      ],
    },
  ],
  experience: [
    {
      role: "AI Engineer",
      org: "Smoky Hill Engineering",
      time: "May 2025 – Present",
      place: "Denver, USA (Remote)",
      bullets: [
        "Designed and implemented end-to-end RAG-based AI solutions for domain knowledge systems.",
        "Built an AI-powered QA chatbot integrating LLM + vector search.",
        "Performed instruction tuning and domain adaptation for construction use cases.",
        "Developed extraction + transformation pipelines for structured/unstructured data.",
        "Improved CI/CD workflows and optimized prompts to reduce hallucinations.",
      ],
      stack: [
        "Python",
        "LangChain",
        "AWS",
        "FastAPI",
        "SQL",
        "Docker",
        "CI/CD",
        "LLM APIs",
      ],
    },
    {
      role: "Sr. Ops Associate (POC)",
      org: "Amazon Corporate Service",
      time: "Jul 2022 – Present",
      place: "Gdansk, Poland (Remote)",
      bullets: [
        "Built Python + PySpark automations reducing manual processing.",
        "Developed ML models for classification, NLP categorization, and predictive workflows.",
        "Designed scalable ETL pipelines for structured/unstructured datasets.",
        "Created internal RAG chatbot for knowledge automation.",
        "Built reverse image search using ResNet50/VGG19 and performance dashboards.",
      ],
      stack: [
        "Python",
        "PySpark",
        "AWS",
        "SQL",
        "TensorFlow",
        "Scikit-learn",
        "CI/CD",
      ],
    },
    {
      role: "Production Engineering Intern",
      org: "ABB Ltd.",
      time: "Jun 2021 – Sep 2021",
      place: "Lodz, Poland",
      bullets: [
        "Supported CNC programming and tool optimization initiatives.",
        "Assisted defect inspection and production efficiency improvements.",
        "Worked with SAP and Teamcenter on production workflow management.",
      ],
      stack: ["CNC", "SAP", "Teamcenter"],
    },
  ],
  projects: [
    {
      title: "RAG-based LLM Chatbot",
      tag: "GenAI • RAG • Vector Search",
      desc: "A domain QA assistant combining retrieval + LLM responses with production-friendly evaluation and deployment hooks.",
      bullets: [
        "Chunking + embedding strategy for accurate retrieval",
        "Prompt + guardrails to reduce hallucinations",
        "API-ready design for integration (e.g., FastAPI)",
      ],
    },
    {
      title: "Reverse Image Search Engine",
      tag: "Computer Vision • Embeddings",
      desc: "Image similarity search using CNN feature embeddings for fast nearest-neighbor retrieval.",
      bullets: [
        "Feature extraction with ResNet50 and VGG19",
        "Similarity scoring and ranked results",
        "Designed for scalable indexing and querying",
      ],
    },
    {
      title: "Text-based Product Categorization",
      tag: "NLP • Classification",
      desc: "NLP pipeline to categorize product text at scale with robust preprocessing and evaluation.",
      bullets: [
        "Text cleaning + tokenization + feature engineering",
        "Model training, evaluation, and error analysis",
        "Deployable inference workflow",
      ],
    },
    {
      title: "Predictive Maintenance from Vibration Signals",
      tag: "Time-series • Signal Processing • ML",
      desc: "Failure prediction using vibration signal features and supervised learning workflows.",
      bullets: [
        "Signal preprocessing and feature extraction",
        "Model training + validation across operating regimes",
        "Thresholding for actionable alerts",
      ],
    },
    {
      title: "Semantic Segmentation with DeepLabV3",
      tag: "Computer Vision • Segmentation",
      desc: "Pixel-wise segmentation pipeline using DeepLabV3 for scene understanding and object delineation.",
      bullets: [
        "Trained/evaluated segmentation model on labeled masks",
        "Augmentation + class imbalance handling",
        "IoU/Dice-based evaluation and tuning",
      ],
    },
    {
      title: "Transformer + LSTM Sentiment Classification",
      tag: "NLP • Deep Learning",
      desc: "Sentiment classifier combining transformer embeddings with sequence modeling for robust text understanding.",
      bullets: [
        "Transformer embeddings feeding LSTM classifier head",
        "Regularization + hyperparameter tuning",
        "Confusion-matrix driven improvements",
      ],
    },
    {
      title: "UAV Flying Object Tracking (MSc Thesis)",
      tag: "Robotics • Tracking • CV",
      desc: "Master’s thesis focused on detecting and tracking flying objects from UAV imagery for robust motion-aware perception.",
      bullets: [
        "Tracking-by-detection pipeline design",
        "Handled motion blur and fast target movement",
        "Evaluation across varied flight conditions",
      ],
    },
    {
      title: "Knowledge Transfer in CNN Image Classification",
      tag: "Deep Learning • Transfer Learning",
      desc: "Explored transfer learning strategies to improve classification performance with limited labeled data.",
      bullets: [
        "Compared frozen vs fine-tuned backbones",
        "Studied domain shift effects on accuracy",
        "Reported gains using pretrained CNN features",
      ],
    },
    {
      title: "IMDb Movie Recommendation System",
      tag: "Recommenders • ML",
      desc: "Movie recommender using IMDb-style metadata and collaborative/content-based strategies.",
      bullets: [
        "Content-based similarity using embeddings/features",
        "Optional collaborative filtering baseline",
        "Top-N recommendation evaluation approach",
      ],
    },
  ],
  education: [
    {
      item: "MSc Robotics & Automation — Warsaw University of Technology (Warsaw, Poland)",
    },
    {
      item: "BSc Electromechanical Engineering — Addis Ababa Science and Technology University (Ethiopia)",
    },
    {
      item: "Polish Language Studies — University of Lodz & University of Silesia (Poland)",
    },
  ],
  certs: [
    "AWS Cloud Certification",
    "AWS Networking Basics",
    "Ignacy Łukasiewicz Scholarship (NAWA, Poland)",
  ],
};
